import { useMutation, useQuery } from 'react-query';
import { queryClient } from '../../infrastructure/queries';
import {
  createCampaign,
  changeStatusOfSelectedCampaigns,
  fetchCampaign,
  fetchCampaigns,
  updateCampaign,
} from './api';
import { _defaults, _uniqueId } from 'underscore-es';

export const useCampaignsQuery = (params, options) => {
  return useQuery(
    ['campaigns', 'index', params],
    () => fetchCampaigns(params),
    options
  );
};

export const useCampaignQuery = (id, options) => {
  return useQuery(['campaigns', id], () => fetchCampaign(id), options);
};

export const useUpdateCampaignMutation = options => {
  return useMutation(({ id, updates }) => updateCampaign(id, updates), {
    ...options,
    onMutate({ id, updates }) {
      const campaignQueryKey = ['campaigns', id];

      const previousCampaign = queryClient.getQueryData(campaignQueryKey);
      const normalizedUpdates = { ...updates };
      if (updates.stages) {
        normalizedUpdates.stages = updates.stages.map(stage => ({
          _id: _uniqueId('stage'),
          ...stage,
          analytics: _defaults(stage.analytics ?? {}, {
            sent: 0,
            opened: 0,
            clicked: 0,
          }),
        }));
      }
      const newCampaign = { ...previousCampaign, ...normalizedUpdates };
      queryClient.setQueryData(['campaigns', id], newCampaign);

      return { previousCampaign };
    },
    onSuccess(_, { id }) {
      queryClient.invalidateQueries(['campaigns', id]);
      queryClient.invalidateQueries(['campaigns', 'index']);
    },
    onError(_, { id }, { previousCampaign }) {
      queryClient.setQueryData(['campaigns', id], previousCampaign);
    },
  });
};

// Autosave mutation that doesn't invalidate queries to prevent unnecessary refetches
export const useAutosaveCampaignMutation = options => {
  return useMutation(({ id, updates }) => updateCampaign(id, updates), {
    ...options,
    onMutate({ id, updates }) {
      const campaignQueryKey = ['campaigns', id];

      const previousCampaign = queryClient.getQueryData(campaignQueryKey);
      const normalizedUpdates = { ...updates };
      if (updates.stages) {
        normalizedUpdates.stages = updates.stages.map(stage => ({
          _id: _uniqueId('stage'),
          ...stage,
          analytics: _defaults(stage.analytics ?? {}, {
            sent: 0,
            opened: 0,
            clicked: 0,
          }),
        }));
      }
      const newCampaign = { ...previousCampaign, ...normalizedUpdates };
      queryClient.setQueryData(['campaigns', id], newCampaign);

      return { previousCampaign };
    },
    // Don't invalidate queries on autosave to prevent unnecessary refetches
    // The optimistic update above is sufficient for keeping UI in sync
    onSuccess() {
      // Only invalidate the index, not the individual campaign query
      // This prevents refetching the campaign while editing
      queryClient.invalidateQueries(['campaigns', 'index']);
    },
    onError(_, { id }, { previousCampaign }) {
      queryClient.setQueryData(['campaigns', id], previousCampaign);
    },
  });
};

export const useCreateCampaignMutation = options => {
  return useMutation(createCampaign, {
    ...options,
    onSuccess() {
      queryClient.invalidateQueries('campaigns');
    },
  });
};

export const useChangeStatusOfSelectedCampaignsMutation = options => {
  return useMutation(changeStatusOfSelectedCampaigns, {
    ...options,
    onSuccess() {
      queryClient.invalidateQueries('campaigns');
    },
  });
};
